import HomeIcon from '@mui/icons-material/Home';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Alert, Button, Snackbar, useTheme } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import Flex from '@Components/Bases/Flex';
import { SIDEBAR_WIDTH } from '@Containers/ReportLayout/Containers/SidebarContainer';
import { useSaveGoalsAndOperationalPlans } from '@Domains/GoalsAndOperationalPlans';

import { deleteAssets, useDeleteImage } from '@Domains/UploadImage';
import { useDisclosure } from '@hooks/useDisclosure';
import { mapGoalsAndOperationalPlansToFormValues } from '@Router/GoalAndOperationalPlans/utils';
import { findUserRoles } from '@Utils/roleUtils';
import { updateCompletedSection } from '@Utils/updateCompletedSection';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { useProfile } from 'src/Constant/UserProfileContext/UserProfileProvider';
import { SectionEnum } from 'src/Enum/SectionEnum';
import { useGoalAndOperationalPlansForm } from '../GoalAndOperationalPlansFormProvider';
import { mapToGoalsAndOperationPlansPayload } from './utils';

const GoalsAndOperationalPlansFooter = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { zIndex } = useTheme();
  const { user } = useProfile();
  const { deleteImage } = useDeleteImage(Number(id));

  const [openedSnackbar, { open: openSnackbar, close: closeSnackbar }] =
    useDisclosure();
  const [{ loading, error }, saveGoalsAndOperationPlans] =
    useSaveGoalsAndOperationalPlans(id);
  const { reset, getValues, trigger } = useGoalAndOperationalPlansForm();
  const { refetchProgressWithUpdate, isPublished, breadcrumbs } =
    useProgressBar();

  const footerHeight = 64;

  const handlePreviewClick = () => {
    if (id) {
      const url = `/report/${id}/preview?section=${SectionEnum.CvUpAndRiskMgmt}`;
      window.open(url, '_blank');
    }
  };

  const handleSave = async () => {
    trigger();
    const formValues = getValues();

    if (user) {
      const { cvupCreatorRole, cvupApproverRole } = findUserRoles(user.N_ROLES);

      const updatedCompletedSection = updateCompletedSection(
        formValues.completedSection,
        cvupCreatorRole ? { N_ROLE: cvupCreatorRole } : undefined,
        cvupApproverRole ? { N_ROLE: cvupApproverRole } : undefined
      );

      const deleteAssetList =
        formValues.companyValueIncreasePlans.valueUpPlans?.flatMap(
          (plan) =>
            plan.assets?.filter((asset) => asset.markedForDeletion) || []
        );

      if (deleteAssetList?.length) {
        deleteAssets(
          'companyValueIncreasePlansValueUpPlans',
          deleteAssetList,
          deleteImage
        );
      }

      if (updatedCompletedSection) {
        const completeFormValues = {
          ...formValues,
          completedSection: updatedCompletedSection,
        };

        const payload = mapToGoalsAndOperationPlansPayload(completeFormValues);

        try {
          const response = await saveGoalsAndOperationPlans({
            data: payload,
          });

          if (response) {
            const data = mapGoalsAndOperationalPlansToFormValues(response.data);
            reset({ ...data }, { keepErrors: true, keepIsValidating: false });
          }
        } catch (error) {
          console.error(error);
        } finally {
          refetchProgressWithUpdate(true);
          openSnackbar();
        }
      }
    }
  };

  return (
    <>
      <Flex
        justifyContent={'space-between'}
        bgcolor={'common.white'}
        p={2}
        height={footerHeight}
        border={0}
        position="fixed"
        bottom={0}
        right={0}
        width={`calc(100vw - ${SIDEBAR_WIDTH}px)`}
        boxShadow={1}
        zIndex={zIndex.appBar}
      >
        <Button
          color="inherit"
          startIcon={<HomeIcon />}
          onClick={() => navigate(`/?report=${breadcrumbs?.reportId}`)}
        >
          กลับสู่หน้าหลัก
        </Button>
        <Flex gap={2}>
          <Button
            color="inherit"
            startIcon={<VisibilityIcon />}
            onClick={handlePreviewClick}
            disabled={isPublished}
          >
            แสดงตัวอย่าง
          </Button>
          <Button
            type="submit"
            color="inherit"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            loading={loading}
            disabled={isPublished}
          >
            บันทึก
          </Button>
        </Flex>
      </Flex>
      <Snackbar
        open={openedSnackbar}
        autoHideDuration={2000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={!!error ? 'error' : 'success'}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '48px',
          }}
        >
          {!!error ? 'เกิดข้อผิดพลาดในการบันทึก' : 'บันทึกสําเร็จ'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GoalsAndOperationalPlansFooter;
